// Super-admin pricing control panel. Per-line markup editing with live margin
// math + a "set markup %" shortcut that recomputes customer price from cost.
import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner, Input } from '../ui.jsx';

const LABEL = {
  image_standard:    'AI Image (standard, FLUX schnell)',
  image_premium:     'AI Image (premium, FLUX 1.1 Pro)',
  video_fast:        'AI Video (short, Wan 2.1 480p)',
  video_fast_hd:     'AI Video (HD short, Wan 2.1 720p)',
  video_standard:    'AI Video (standard, Kling 1.6)',
  video_premium:     'AI Video (premium cinematic, Higgsfield Soul)',
  sms_segment:       'SMS segment',
  voice_per_min:     'Voice call (per minute)',
  voice_ai_per_min:  'AI voice (Conversational, per minute)',
  whisper_per_min:   'Whisper transcription (per minute)',
  email_per_send:    'Email send',
};

export function AgencyPricing({ toast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const load = () => api('/api/agency/pricing').then(setData).catch(()=>{});
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner sz={32}/>;

  const save = async (key, customerCents) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await api('/api/agency/pricing', { method:'PATCH', body:{ key, customerCents }}); await load(); toast('✓ Saved'); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  return <div style={{ padding:24, maxWidth:980 }}>
    <h1 style={{ margin:'0 0 8px', fontSize:24, fontWeight:800 }}>💰 Platform Pricing</h1>
    <div style={{ color:G.textMuted, fontSize:14, marginBottom:20 }}>
      What customers see + pay per metered unit. Change a customer price to override the default; revert any time. All numbers in cents (¢).
    </div>

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr style={{ background:G.input }}>
          {['Line','Our Cost','Customer Price','Markup %','Margin / unit','Status',''].map(h =>
            <th key={h} style={{ ...styles.th, fontSize:11, padding:'10px 12px' }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {data.rateCard.map(line => <PricingRow key={line.key} line={line} busy={busy[line.key]} onSave={save}/>)}
        </tbody>
      </table>
    </div>

    <div style={{ marginTop:16, fontSize:12, color:G.textMuted, lineHeight:1.6 }}>
      💡 <strong style={{color:G.text}}>Tip:</strong> Use the markup % field to set a price relative to cost. Direct customer-cents edits override that. Changes apply within 30 seconds to all dashboards + the billing authorization form.
    </div>
  </div>;
}

function PricingRow({ line, busy, onSave }) {
  const [customer, setCustomer] = useState(line.customerCents);
  const [markupPct, setMarkupPct] = useState(line.ourCostCents > 0 ? Math.round((line.customerCents / line.ourCostCents - 1) * 100) : 0);
  useEffect(() => {
    setCustomer(line.customerCents);
    setMarkupPct(line.ourCostCents > 0 ? Math.round((line.customerCents / line.ourCostCents - 1) * 100) : 0);
  }, [line.customerCents, line.ourCostCents]);

  const margin = customer - line.ourCostCents;
  const marginPct = customer > 0 ? Math.round((margin / customer) * 100) : 0;
  const setMarkup = (pct) => {
    setMarkupPct(pct);
    if (line.ourCostCents > 0) setCustomer(Math.round(line.ourCostCents * (1 + pct/100) * 10) / 10);
  };
  const dirty = Math.abs(customer - line.customerCents) > 0.001;

  return <tr style={{ borderTop:`1px solid ${G.border}` }}>
    <td style={{ padding:'10px 12px', fontSize:13 }}>
      <div style={{ fontWeight:600 }}>{LABEL[line.key] || line.key}</div>
      <div style={{ fontSize:11, color:G.textMuted }}>{line.key}</div>
    </td>
    <td style={{ padding:'10px 12px', fontSize:13, color:G.textMuted, fontFamily:'monospace' }}>{line.ourCostCents.toFixed(2)}¢</td>
    <td style={{ padding:'10px 12px' }}>
      <Input type="number" step="0.1" value={customer}
        onChange={e=>{ const v = Number(e.target.value); setCustomer(v); if (line.ourCostCents > 0) setMarkupPct(Math.round((v / line.ourCostCents - 1) * 100)); }}
        style={{ width:90, fontFamily:'monospace' }}/>
    </td>
    <td style={{ padding:'10px 12px' }}>
      <Input type="number" step="5" value={markupPct} onChange={e=>setMarkup(Number(e.target.value))} style={{ width:80, fontFamily:'monospace' }}/>
    </td>
    <td style={{ padding:'10px 12px', fontSize:13, color: margin>=0?G.green:G.red, fontWeight:700, fontFamily:'monospace' }}>
      {margin.toFixed(2)}¢ · {marginPct}%
    </td>
    <td style={{ padding:'10px 12px', fontSize:11 }}>
      {line.overridden
        ? <span style={{ color:G.purple, fontWeight:700 }}>OVERRIDE</span>
        : <span style={{ color:G.textMuted }}>default</span>}
    </td>
    <td style={{ padding:'10px 12px' }}>
      <div style={{ display:'flex', gap:6 }}>
        <Btn sz="xs" v="primary" disabled={busy || !dirty} onClick={()=>onSave(line.key, customer)}>Save</Btn>
        {line.overridden && <Btn sz="xs" v="ghost" disabled={busy} onClick={()=>onSave(line.key, null)}>Revert</Btn>}
      </div>
    </td>
  </tr>;
}
